import { createRootRoute, createRoute, createRouter } from "@tanstack/react-router";
import type React from "react";
import { RootLayout } from "./routes/__root";
import { AddressPage } from "./routes/address";
import { AppPreviewPage } from "./routes/app";
import { BeginnerGuidePage } from "./routes/beginner-guide";
import { BillPage } from "./routes/bill";
import { CartPage } from "./routes/cart";
import { CatalogPage } from "./routes/catalog";
import { ConfirmPayPage } from "./routes/confirm-pay";
import { EstimationPage } from "./routes/estimation";
import { FavoritePage } from "./routes/favorite";
import { ForgotPage } from "./routes/forgot";
import { HelpPage } from "./routes/help";
import { IndexPage } from "./routes/index";
import { InvitePage } from "./routes/invite";
import { LoginPage } from "./routes/login";
import { MessagePage } from "./routes/message";
import { NoticePage } from "./routes/notice";
import { OrderDetailPage } from "./routes/order-detail";
import { OrdersPage } from "./routes/orders";
import { PayCancelPage } from "./routes/pay-cancel";
import { PayResultPage } from "./routes/pay-result";
import { PaySuccessPage } from "./routes/pay-success";
import { PointsPage } from "./routes/points";
import { ProductPage } from "./routes/product";
import { ProfilePage } from "./routes/profile";
import { RegisterPage } from "./routes/register";
import { RehearsalPage } from "./routes/rehearsal";
import { ShipmentPage } from "./routes/shipment";
import { ShopwindowPage } from "./routes/shopwindow";
import { SubmitParcelPage } from "./routes/submit-parcel";
import { SupplementaryPaymentPage } from "./routes/supplementary-payment";
import { TransportPage } from "./routes/transport";
import { WalletPage } from "./routes/wallet";
import { WarehousePage } from "./routes/warehouse";
import { WithdrawPage } from "./routes/withdraw";

const rootRoute = createRootRoute({ component: RootLayout });

// Thin helper so route declarations stay flat. `path` is cast because each
// concrete literal would need its own generic propagation; we accept that
// trade-off for readability.
function leaf(path: string, component: () => React.ReactElement | null) {
  return createRoute({
    getParentRoute: () => rootRoute,
    path: path as never,
    component,
  });
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: IndexPage,
});

const routes = [
  indexRoute,
  leaf("/app", AppPreviewPage),
  // Browse / shopping
  leaf("/catalog", CatalogPage),
  leaf("/product/$channel/$spuNo", ProductPage),
  leaf("/cart", CartPage),
  leaf("/confirm-pay", ConfirmPayPage),
  // Pay outcomes
  leaf("/pay-success", PaySuccessPage),
  leaf("/pay-cancel", PayCancelPage),
  leaf("/pay-result", PayResultPage),
  // Orders
  leaf("/orders", OrdersPage),
  leaf("/order-detail/$orderNo", OrderDetailPage),
  leaf("/supplementary-payment", SupplementaryPaymentPage),
  // Logistics + warehouse
  leaf("/warehouse", WarehousePage),
  leaf("/submit-parcel", SubmitParcelPage),
  leaf("/shipment", ShipmentPage),
  leaf("/transport", TransportPage),
  leaf("/estimation", EstimationPage),
  // Account
  leaf("/profile", ProfilePage),
  leaf("/favorite", FavoritePage),
  leaf("/address", AddressPage),
  leaf("/wallet", WalletPage),
  leaf("/withdraw", WithdrawPage),
  leaf("/bill", BillPage),
  leaf("/message", MessagePage),
  leaf("/notice", NoticePage),
  // Auth
  leaf("/login", LoginPage),
  leaf("/register", RegisterPage),
  leaf("/forgot", ForgotPage),
  // Marketing
  leaf("/invite", InvitePage),
  leaf("/points", PointsPage),
  leaf("/rehearsal", RehearsalPage),
  leaf("/shopwindow", ShopwindowPage),
  // Info
  leaf("/help", HelpPage),
  leaf("/beginner-guide", BeginnerGuidePage),
];

// TanStack Router infers a readonly tuple for `addChildren` to give typed
// routes; we keep route list in a regular array (~30 entries) and cast —
// typed `Link to` is achieved via per-call `as never` casts.
// biome-ignore lint/suspicious/noExplicitAny: see above
const routeTree = rootRoute.addChildren(routes as any);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
